import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box, Typography, Divider, Stack, Alert, Button, IconButton, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Tooltip,
} from '@mui/material';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { formatPaise, describeLineUnits } from '../api/billingApi';
import QuantityStepper from '../components/NexusQuantityStepper';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, EmptyState, GradientButton, fadeUp,
} from '../components/NextGenPortalUI';

/**
 * NextGenCart — /nextgen-builder/cart
 *
 * A plain, honest order table: what is being bought, at what unit price, how
 * many, and what it totals. Every number is the server's — each mutation
 * returns the recomputed cart and the page simply re-renders it.
 */

const ItemDetails = ({ item }) => (
  <Stack spacing={0.4}>
    <Typography variant="body2" sx={{ color: TOKENS.text.caption, fontWeight: 600, fontSize: '0.8rem' }}>
      {item.accessType === 'FULL_ACCESS' ? `${describeLineUnits(item)} · one technology` : `${describeLineUnits(item)} · ${item.unitsPerPurchase} per purchase`}
    </Typography>
    {item.technologyTitle && (
      <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
        Technology: {item.technologyTitle}
      </Typography>
    )}
    {item.pricing && item.pricing.tier && item.pricing.tier.name && (
      <Chip
        size="small"
        label={`${item.pricing.tier.name} pricing`}
        sx={{
          alignSelf: 'flex-start', height: 20, fontWeight: 700, fontSize: '0.65rem',
          bgcolor: 'rgba(245,158,11,0.12)', color: '#B45309', border: '1px solid rgba(245,158,11,0.26)',
        }}
      />
    )}
  </Stack>
);

const NextGenCart = () => {
  const navigate = useNavigate();
  const { cart, catalog, loading, updateCartItem, removeCartItem, refreshCart } = useNexusBilling();
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const items = cart && Array.isArray(cart.items) ? cart.items : [];
  const totals = cart && cart.totals ? cart.totals : null;
  const taxRate = cart && cart.taxRate ? cart.taxRate : (catalog ? catalog.taxRate : null);
  const hasUnavailable = items.some((i) => i.unavailable);

  /** maxQuantityPerOrder lives on the catalog product, not on the cart line. */
  const maxQtyFor = useCallback((productCode) => {
    if (!catalog || !Array.isArray(catalog.features)) return null;
    for (const f of catalog.features) {
      const p = (f.products || []).find((x) => x.code === productCode);
      if (p && p.maxQuantityPerOrder) return p.maxQuantityPerOrder;
    }
    return null;
  }, [catalog]);

  const runMutation = useCallback(async (itemId, fn) => {
    setBusyId(itemId);
    setError('');
    try {
      await fn();
    } catch (e) {
      if (e && e.status === 401) { navigate('/nextgen-builder/login', { state: { from: '/nextgen-builder/cart' } }); return; }
      setError((e && e.message) || 'Could not update your cart');
    } finally {
      setBusyId(null);
    }
  }, [navigate]);

  const handleQuantity = (item, quantity) => runMutation(item.id, () => updateCartItem(item.id, quantity));
  const handleRemove = (item) => runMutation(item.id, () => removeCartItem(item.id));

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try { await refreshCart(); } finally { setRefreshing(false); }
  };

  const busy = busyId != null || refreshing;
  const showSkeleton = !cart && loading;

  const totalsRows = useMemo(() => {
    if (!totals) return [];
    return [
      { label: 'Subtotal', value: formatPaise(totals.subtotalPaise) },
      { label: taxRate ? `${taxRate.name || taxRate.code} (${taxRate.percent}%)` : 'Tax', value: formatPaise(totals.taxPaise) },
    ];
  }, [totals, taxRate]);

  return (
    <PageContainer maxWidth={1140}>
      <PageHeader
        icon={<ShoppingCartRoundedIcon />}
        eyebrow="TSG Nexus"
        title="Your cart"
        description="Review your feature credits before checkout. Prices are recalculated by our servers every time this page loads."
        action={
          <Button
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
              color: TOKENS.text.caption, border: `1px solid ${TOKENS.border}`, px: 2,
              '&:hover': { bgcolor: TOKENS.bg.page },
            }}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: TOKENS.radius.control }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {hasUnavailable && (
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ mb: 3, borderRadius: TOKENS.radius.control }}>
          One or more items are no longer available. Remove them to continue to checkout.
        </Alert>
      )}

      {showSkeleton ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: TOKENS.radius.card }} />
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: TOKENS.radius.card }} />
        </Stack>
      ) : items.length === 0 ? (
        <SectionCard component={motion.div} {...fadeUp}>
          <EmptyState
            icon={<ShoppingCartRoundedIcon />}
            title="Your cart is empty"
            description="Feature credits you add from the Feature credits page will appear here."
            action={
              <GradientButton onClick={() => navigate('/nextgen-builder/plans')} sx={{ textTransform: 'none' }}>
                Browse feature credits
              </GradientButton>
            }
          />
        </SectionCard>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
          {/* ── Lines ── */}
          <SectionCard component={motion.div} {...fadeUp} padding={0} sx={{ p: 0, overflow: 'hidden' }}>
            {/* Desktop / tablet table */}
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 800, color: TOKENS.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${TOKENS.border}` } }}>
                    <TableCell>Product</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        bgcolor: item.unavailable ? 'rgba(245,158,11,0.06)' : 'transparent',
                        '& td': { borderBottom: `1px solid ${TOKENS.borderSoft}`, py: 1.75 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.87rem' }}>
                          {item.productName}
                        </Typography>
                        {item.unavailable && item.reason && (
                          <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 700, display: 'block', mt: 0.4 }}>
                            {item.reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell><ItemDetails item={item} /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: TOKENS.text.secondary, fontSize: '0.85rem' }}>
                          {formatPaise(item.unitPricePaise)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <QuantityStepper
                          value={item.quantity}
                          max={maxQtyFor(item.productCode)}
                          disabled={busy || item.unavailable}
                          onChange={(q) => handleQuantity(item, q)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.9rem' }}>
                          {formatPaise(item.subtotalPaise)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => handleRemove(item)}
                              sx={{ color: TOKENS.text.muted, '&:hover': { color: '#DC2626', bgcolor: 'rgba(239,68,68,0.08)' } }}
                            >
                              {busyId === item.id ? <CircularProgress size={16} /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile cards */}
            <Stack sx={{ display: { xs: 'flex', md: 'none' } }} divider={<Divider />}>
              {items.map((item) => (
                <Box key={item.id} sx={{ p: 2.25, bgcolor: item.unavailable ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                      {item.productName}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => handleRemove(item)}
                      aria-label="Remove item"
                      sx={{ color: TOKENS.text.muted, mt: -0.5 }}
                    >
                      {busyId === item.id ? <CircularProgress size={16} /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Box sx={{ mt: 0.75, mb: 1.25 }}><ItemDetails item={item} /></Box>
                  {item.unavailable && item.reason && (
                    <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 700, display: 'block', mb: 1 }}>
                      {item.reason}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <QuantityStepper
                      value={item.quantity}
                      max={maxQtyFor(item.productCode)}
                      disabled={busy || item.unavailable}
                      onChange={(q) => handleQuantity(item, q)}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', fontWeight: 600 }}>
                        {formatPaise(item.unitPricePaise)} each
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                        {formatPaise(item.subtotalPaise)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </SectionCard>

          {/* ── Totals ── */}
          <SectionCard component={motion.div} {...fadeUp} sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem', mb: 2 }}>
              Order summary
            </Typography>
            <Stack spacing={1.1}>
              {totalsRows.map((row) => (
                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>{row.label}</Typography>
                  <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 700 }}>{row.value}</Typography>
                </Box>
              ))}
            </Stack>
            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary }}>Grand total</Typography>
              <Typography sx={{ fontWeight: 900, color: TOKENS.text.primary, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
                {totals ? formatPaise(totals.grandTotalPaise) : '—'}
              </Typography>
            </Box>

            <GradientButton
              fullWidth
              onClick={() => navigate('/nextgen-builder/checkout')}
              disabled={busy || items.length === 0 || hasUnavailable}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '17px !important' }} />}
              sx={{ mt: 2.5, py: 1.25, textTransform: 'none', '&.Mui-disabled': { background: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } }}
            >
              Proceed to checkout
            </GradientButton>
            <Button
              fullWidth
              onClick={() => navigate('/nextgen-builder/plans')}
              sx={{
                mt: 1.25, borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
                color: TOKENS.text.caption, border: `1px solid ${TOKENS.border}`,
                '&:hover': { bgcolor: TOKENS.bg.page },
              }}
            >
              Continue shopping
            </Button>

            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: TOKENS.text.muted, fontWeight: 500 }}>
              {cart ? `${cart.itemCount} ${cart.itemCount === 1 ? 'item' : 'items'} across ${cart.lineCount} ${cart.lineCount === 1 ? 'line' : 'lines'}.` : ''} Credits are added to your account only after payment is verified.
            </Typography>
          </SectionCard>
        </Box>
      )}
    </PageContainer>
  );
};

export default NextGenCart;
